import React, { useState } from "react";
import { Spinner } from "react-bootstrap";
import apiPublic from "../../apiPublic";
import "./PublicRespondentGate.css";

// Public Form Respondent Identity — shown BEFORE any form question, for
// every /form/f/:token visit. The backend re-verifies this same email on
// every later draft-save/submit call too (services/publicFormAccessController.js) —
// this gate is the friendly first attempt, not the only enforcement point,
// so there is nothing for a caller to "get past" by skipping this UI.
//
// Only ever calls POST /public/form-access/:token/verify-email — never
// reads/writes any user_id, and never shows a name until the backend has
// confirmed the entered email belongs to THIS link's own assignee (a
// mismatch or unknown email gets the exact same two generic messages the
// backend already chose; see services/publicFormRespondentService.js).
//
// `onLinkError(code, message)` fires for anything that isn't an email-
// specific rejection (an expired/revoked/invalid token, still checked on
// every call since the token middleware runs before this endpoint's own
// logic) — the parent page owns showing its existing full-page link-state
// screens for those, exactly as it already does for the initial form load.
const EMAIL_CODES = new Set([
  "PUBLIC_LINK_EMAIL_REQUIRED",
  "PUBLIC_LINK_EMAIL_INVALID",
  "PUBLIC_LINK_EMAIL_UNVERIFIED",
  "PUBLIC_LINK_EMAIL_MISMATCH",
]);

export default function PublicRespondentGate({ token, onVerified, onLinkError }) {
  const [email, setEmail] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState(null);
  const [identity, setIdentity] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (verifying) return;
    setVerifying(true);
    setError(null);
    try {
      const res = await apiPublic.post(`/public/form-access/${token}/verify-email`, { email });
      setIdentity(res.data?.data || null);
    } catch (err) {
      const code = err?.response?.data?.code;
      const message = err?.response?.data?.message;
      if (code && !EMAIL_CODES.has(code)) {
        onLinkError(code, message);
        return;
      }
      setError(message || "We could not verify this email for this feedback link.");
    } finally {
      setVerifying(false);
    }
  };

  if (identity) {
    return (
      <div className="prg-card prg-card-verified">
        <div className="prg-verified-icon" aria-hidden="true">✓</div>
        <h3>Identity Verified</h3>
        <p className="prg-verified-label">Submitting as</p>
        <p className="prg-verified-name">{identity.name}</p>
        <p className="prg-verified-email">{identity.email}</p>
        <button type="button" className="prg-btn prg-btn-continue" onClick={() => onVerified(identity)}>
          Continue to Form
        </button>
      </div>
    );
  }

  return (
    <div className="prg-card">
      <h3>Verify Your Email</h3>
      <p className="prg-hint">We use this email to identify who is submitting this feedback.</p>
      <form onSubmit={handleSubmit} className="prg-form">
        <label htmlFor="prg-email" className="prg-label">Your Email Address *</label>
        <input
          id="prg-email"
          type="email"
          required
          autoComplete="email"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="prg-input"
          disabled={verifying}
        />
        {error && <div className="prg-error" role="alert">{error}</div>}
        <button type="submit" className="prg-btn prg-btn-submit" disabled={verifying || !email.trim()}>
          {verifying ? <><Spinner animation="border" size="sm" /> Verifying…</> : "Continue"}
        </button>
      </form>
    </div>
  );
}
